import { useMemo } from 'react';
import useSWR from 'swr';
import * as _ from 'lodash';
import { createBatchResolver } from '../utils/batch-resolver';
import { getBoards, getAllBoards, Board } from '@gorilla/common/src/lib/monday-api/api';
import { getConnectedBoardIds } from '@gorilla/common/src/lib/engine/engine';
import { mondayClient } from '../../services/monday';

export type BoardWithFolder = Board & { path: string[]; connected_board_ids: number[] };

export const boardBatchResolver = createBatchResolver<number, BoardWithFolder | undefined>(async (boardIds: number[]) => {
  const boards = await getBoards(mondayClient, boardIds);
  const boardsById = _.keyBy(boards, 'id');

  return boardIds.reduce(
    (memo, boardId) => {
      const board = boardsById[boardId];

      if (!board) {
        memo[boardId] = undefined;
        return memo;
      }

      memo[boardId] = {
        ...board,
        path: [],
        connected_board_ids: getConnectedBoardIds(board),
      };

      return memo;
    },
    {} as Record<number, BoardWithFolder | undefined>,
  );
});

export function useBoards(boardIds: number[], errorIfNotFound = true) {
  let {
    data: boards,
    isLoading,
    error,
  } = useSWR(`/boards/${boardIds.join('/')}`, async () => (boardIds.length ? boardBatchResolver.resolveItems(boardIds) : []), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });

  if (boards && errorIfNotFound && boards.some((board) => !board)) {
    boards = undefined;
    error = new Error('Some boards not found');
  }

  return {
    boards: boards ? (boards.filter((board) => board) as BoardWithFolder[]) : boards,
    isLoading,
    error,
  };
}

export function useBoard(boardId?: number) {
  const { boards, isLoading, error } = useBoards(boardId ? [boardId] : []);

  return {
    board: boards?.[0],
    isLoading,
    error,
  };
}

export function useSingleBoard(boardId?: number) {
  const {
    data: board,
    mutate,
    isLoading,
    error,
  } = useSWR(
    boardId ? `/single-board/${boardId}` : null,
    async () => {
      if (!boardId) {
        return undefined;
      }

      const boards = await getBoards(mondayClient, [boardId]);

      if (!boards.length) {
        return null;
      }

      return boards[0];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  return {
    mutate,
    board,
    isLoading,
    error,
  };
}

export function useSubboard(mainBoardId?: number) {
  const { board: mainBoard } = useBoard(mainBoardId);
  let subboardId = undefined;

  if (mainBoard) {
    const subtasksColumn = mainBoard.columns.find((column) => column.type === 'subtasks');

    if (subtasksColumn) {
      try {
        const settings = JSON.parse(subtasksColumn.settings_str);
        if (settings && settings.boardIds && settings.boardIds.length) {
          subboardId = settings.boardIds[0];
        }
      } catch (err) {}
    }
  }

  const { board: subboard, isLoading, error } = useBoard(subboardId);

  return {
    subboard,
    isLoading,
    error,
  };
}

export function useAllBoards() {
  // TODO: filter out boards that are not of type 'sub_items_board' or 'board'
  const { data: boardsUnfiltered, error } = useSWR(`/all-boards`, async () => _.orderBy(await getAllBoards(mondayClient), 'name'), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });

  const boards = useMemo(() => {
    if (!boardsUnfiltered) {
      return boardsUnfiltered;
    }

    return (boardsUnfiltered || []).filter((board) => board.type === 'board' || board.type === 'sub_items_board');
  }, [boardsUnfiltered]);

  const workspaces = useMemo(
    () =>
      _.uniqBy(
        (boards || []).map((board) => board.workspace),
        'id',
      ),
    [boards],
  );

  const boardsById = useMemo(() => _.keyBy(boards || [], 'id'), [boards]);

  function getBoardNameFromId(boardId: number) {
    if (boardsById[boardId]) {
      return boardsById[boardId].name;
    }

    return `Board ${boardId}`;
  }

  return {
    workspaces,
    isLoading: !boards,
    getBoardNameFromId,
    boards,
    boardsById,
    error,
  };
}
